import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '@/context/AuthContext';
import {
  WorkDay,
  TimeEntry,
  subscribeWorkDay,
  subscribeActiveEntry,
  subscribeEntriesForDay,
  startWorkDay as svcStartWorkDay,
  endWorkDay as svcEndWorkDay,
  startTimeEntry as svcStartTimeEntry,
  stopTimeEntry as svcStopTimeEntry,
} from '@/services/timeTrackingService';

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export type DayStatus = 'none' | 'open' | 'closed';

interface TimeTrackingContextValue {
  today: string;
  dayStatus: DayStatus;
  workDay: WorkDay | null;
  activeEntry: TimeEntry | null;
  entries: TimeEntry[];
  /** Live elapsed seconds of the running entry (ticks every second). */
  activeElapsed: number;
  /** Total seconds tracked today (finished entries + running). */
  totalToday: number;
  startDay: () => Promise<void>;
  endDay: () => Promise<void>;
  startTask: (taskId: string | null, taskTitle: string) => Promise<void>;
  stopActive: () => Promise<void>;
}

const TimeTrackingContext = createContext<TimeTrackingContextValue | undefined>(undefined);

export const TimeTrackingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const uid = currentUser?.uid || null;

  const [today, setToday] = useState<string>(todayStr());
  const [workDay, setWorkDay] = useState<WorkDay | null>(null);
  const [activeEntry, setActiveEntry] = useState<TimeEntry | null>(null);
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [nowMs, setNowMs] = useState<number>(Date.now());

  // Roll over "today" at midnight (checked every 30s).
  useEffect(() => {
    const id = setInterval(() => {
      const t = todayStr();
      setToday((prev) => (prev !== t ? t : prev));
    }, 30000);
    return () => clearInterval(id);
  }, []);

  // Subscriptions scoped to the current user + day.
  useEffect(() => {
    if (!uid) {
      setWorkDay(null);
      setActiveEntry(null);
      setEntries([]);
      return;
    }
    const unsubDay = subscribeWorkDay(uid, today, setWorkDay);
    const unsubActive = subscribeActiveEntry(uid, setActiveEntry);
    const unsubEntries = subscribeEntriesForDay(uid, today, setEntries);
    return () => {
      unsubDay();
      unsubActive();
      unsubEntries();
    };
  }, [uid, today]);

  // Tick every second only while something is running.
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (activeEntry) {
      setNowMs(Date.now());
      tickRef.current = setInterval(() => setNowMs(Date.now()), 1000);
    }
    return () => {
      if (tickRef.current) {
        clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
  }, [activeEntry?.id]);

  const dayStatus: DayStatus = workDay?.status === 'open'
    ? 'open'
    : workDay?.status === 'closed'
      ? 'closed'
      : 'none';

  const activeElapsed = useMemo(() => {
    if (!activeEntry?.startedAt) return 0;
    return Math.max(0, Math.round((nowMs - activeEntry.startedAt.toMillis()) / 1000));
  }, [activeEntry, nowMs]);

  const totalToday = useMemo(() => {
    let sum = 0;
    for (const e of entries) {
      if (e.endedAt) sum += e.durationSeconds || 0;
    }
    // Add the live running entry (not yet in `entries` with a duration).
    if (activeEntry && !activeEntry.endedAt) sum += activeElapsed;
    return sum;
  }, [entries, activeEntry, activeElapsed]);

  const startDay = async () => {
    if (!uid) return;
    await svcStartWorkDay(uid, today);
    // Optimistic: flip UI immediately; snapshot will reconcile.
    setWorkDay((prev) => ({
      day: today,
      status: 'open',
      startedAt: prev?.startedAt ?? Timestamp.now(),
      endedAt: null,
    }));
  };

  const endDay = async () => {
    if (!uid) return;
    if (activeEntry) {
      await svcStopTimeEntry(uid, activeEntry.id);
      setActiveEntry(null);
    }
    await svcEndWorkDay(uid, today);
    setWorkDay((prev) => ({
      day: today,
      status: 'closed',
      startedAt: prev?.startedAt ?? null,
      endedAt: Timestamp.now(),
    }));
  };

  const startTask = async (taskId: string | null, taskTitle: string) => {
    if (!uid) return;
    const id = await svcStartTimeEntry(uid, { taskId, taskTitle, day: today });
    setActiveEntry({
      id,
      taskId,
      taskTitle: taskTitle || 'Untitled task',
      day: today,
      startedAt: Timestamp.now(),
      endedAt: null,
      durationSeconds: 0,
    });
  };

  const stopActive = async () => {
    if (!uid || !activeEntry) return;
    const stopped = activeEntry;
    await svcStopTimeEntry(uid, stopped.id);
    setActiveEntry(null);
    // Reflect the finished session in today's list immediately.
    const startMs = stopped.startedAt?.toMillis?.() ?? Date.now();
    const durationSeconds = Math.max(0, Math.round((Date.now() - startMs) / 1000));
    setEntries((prev) => {
      const withoutDup = prev.filter((e) => e.id !== stopped.id);
      return [{ ...stopped, endedAt: Timestamp.now(), durationSeconds }, ...withoutDup];
    });
  };

  const value: TimeTrackingContextValue = {
    today,
    dayStatus,
    workDay,
    activeEntry,
    entries,
    activeElapsed,
    totalToday,
    startDay,
    endDay,
    startTask,
    stopActive,
  };

  return <TimeTrackingContext.Provider value={value}>{children}</TimeTrackingContext.Provider>;
};

export const useTimeTracking = (): TimeTrackingContextValue => {
  const ctx = useContext(TimeTrackingContext);
  if (!ctx) throw new Error('useTimeTracking must be used within a TimeTrackingProvider');
  return ctx;
};
